import React, { useMemo } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFieldArrayRemove,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
  useFieldArray,
} from 'react-hook-form';
import {
  Button,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';

import {
  CheckBoxField,
  Section,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import Close from '@mui/icons-material/Close';
import { Add, Remove } from '@mui/icons-material';

import { CapacityFormData } from './CapacityDashboardFilters';
import { useCompany } from '@dispatch/shared/hooks';
import { MultiSelectOption } from '@dispatch/shared/types';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

interface SelectAttributeValuesVarcharsCapacityForm {
  integrationGUID?: string;
  selectedAttributeDefinitionIntegrationGUID: string;
  attributeDefinitionValueIntegrationGUID: string;
  title: string;
  value: string | undefined;
  displayName?: string;
}

const emptyAttributeValueVarCharForm = (
  selectedAttributeDefinitionIntegrationGUID: string
): SelectAttributeValuesVarcharsCapacityForm => {
  return {
    selectedAttributeDefinitionIntegrationGUID:
      selectedAttributeDefinitionIntegrationGUID,
    attributeDefinitionValueIntegrationGUID: '',
    title: '',
    value: '',
  };
};

export type CapacityAttributesFormProps = {
  control: Control<FieldValues>;
  //permissions: Ability<[Actions, Subjects]> | undefined;
  index: number;
  removeAttribute: UseFieldArrayRemove;
  watch: UseFormWatch<CapacityFormData>;
  field: Record<'id', string>;
  errors: FieldErrors;
  getValues: UseFormGetValues<CapacityFormData>;
  setValue: UseFormSetValue<CapacityFormData>;
};

export function CapacityAttributesForm({
  control,
  //permissions,
  index,
  removeAttribute,
  watch,
  field,
  errors,
  getValues,
  setValue,
}: CapacityAttributesFormProps) {
  const { company } = useCompany();
  const {
    fields: attributeValueFields,
    remove: attributeValueRemove,
    append: attributeValueAppend,
  } = useFieldArray({
    control: control,
    name: `attributes.${index}.selectedAttributeValuesVarchars`,
    keyName: 'fieldId',
  });

  const companyAttribute = company?.attributeDefinition.find(
    (attributeDef) =>
      watch(`attributes.${index}`).attributeDefinitionIntegrationGUID ===
      attributeDef.integrationGUID
  );

  const deleteAttribute = (index: number) => {
    removeAttribute(index);
    setValue('addSelectedAttributeGUID', []);
  };

  const isSelectedVarChar =
    companyAttribute?.attributeDataType?.includes('string');

  const addAttributeValue = () => {
    attributeValueAppend(
      emptyAttributeValueVarCharForm(companyAttribute?.integrationGUID || '')
    );
  };

  //to avoid re-renders as array changes length and causes re-render
  const selectedAttributeValuesVarchars = watch(
    `attributes.${index}.selectedAttributeValuesVarchars`
  );

  const disableAdd = useMemo(() => {
    return (
      selectedAttributeValuesVarchars?.length ===
      companyAttribute?.attributeDefinitionValues?.length
    );
  }, [
    companyAttribute?.attributeDefinitionValues?.length,
    selectedAttributeValuesVarchars?.length,
  ]);

  const options = useMemo(() => {
    const currentValues = selectedAttributeValuesVarchars || [];

    const selectedGuids = currentValues
      .map((item) => item?.attributeDefinitionValueIntegrationGUID)
      .filter(Boolean);

    // Filter out already selected languages
    const filteredAttributes =
      companyAttribute?.attributeDefinitionValues?.filter(
        (companyAttr) => !selectedGuids.includes(companyAttr.integrationGUID)
      );

    return (
      filteredAttributes?.map((value) => ({
        title: value.displayName,
        value: value.integrationGUID,
      })) || []
    );
  }, [
    companyAttribute?.attributeDefinitionValues,
    selectedAttributeValuesVarchars,
  ]);

  const deleteAttributeValue = (index: number) => {
    attributeValueRemove(index);
  };

  const onValueChange = (
    value: MultiSelectOption | (string | number)[] | null,
    valueIndex: number
  ) => {
    const option = value as MultiSelectOption;

    setValue(
      `attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}`,
      {
        selectedAttributeDefinitionIntegrationGUID:
          companyAttribute?.integrationGUID || '',
        attributeDefinitionValueIntegrationGUID: option.value as string,
        title: option.title,
        value: option.value as string,
      }
    );
  };

  const MUIMenu = ({
    selectedAttributeVarcharIndex,
    attributeValueRemove,
  }: {
    selectedAttributeVarcharIndex: number;
    attributeValueRemove: UseFieldArrayRemove;
  }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <div key={selectedAttributeVarcharIndex}>
        <Button
          id="basic-button"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          sx={{ color: '#757575' }}
        >
          <MoreHorizIcon />
        </Button>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem
            onClick={() => {
              attributeValueRemove(selectedAttributeVarcharIndex);
              handleClose();
            }}
          >
            Remove Attribute
          </MenuItem>
        </Menu>
      </div>
    );
  };

  const childFieldError =
    errors?.['attributes']?.[index]?.selectedAttributeValuesVarchars?.message;

  const renderAttributes = () => {
    return (
      <>
        <div
          style={{
            padding: '24px',
          }}
        >
          <Grid container spacing={4} xs={12} key={field.id}>
            <Grid item xs={6} data-testid="attribute-name">
              <TextField
                id={`displayName`}
                name={`attributes.${index}.displayName`}
                label="Attribute"
                type="text"
                control={control}
                inputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton onClick={() => deleteAttribute(index)}>
                      <Close />
                    </IconButton>
                  ),
                }}
                errors={errors}
                error={childFieldError}
              />
            </Grid>
            <Grid item xs={4}>
              <IconButton
                onClick={() => addAttributeValue()}
                disabled={disableAdd}
              >
                <Add />
              </IconButton>
            </Grid>
            <Grid container item xs={12} data-testid="attribute-form-varchar">
              {isSelectedVarChar &&
                attributeValueFields.map((attributeValueField, valueIndex) => {
                  return (
                    <div
                      key={`${attributeValueField.fieldId}-${valueIndex}`}
                      style={{ width: '100%' }}
                    >
                      {renderAttribute(attributeValueField, valueIndex)}
                      {/* {renderSubAttribute(valueIndex)} */}
                    </div>
                  );
                })}
            </Grid>
            {!isSelectedVarChar && (
              <Grid container item xs={6} data-testid="attribute-form-boolean">
                <CheckBoxField
                  id={`selectedAttributeValuesBools.value`}
                  name={`attributes.${index}.selectedAttributeValuesBools.value`}
                  label="Active"
                  //disabled={!hasUpsertPermissions}
                  control={control}
                />
              </Grid>
            )}
          </Grid>
        </div>
        <Divider />
      </>
    );
  };

  const renderAttribute = (
    attributeValueField: Record<'fieldId', string>,
    valueIndex: number
  ) => {
    return (
      <Grid
        container
        item
        xs={12}
        spacing={2}
        key={attributeValueField.fieldId}
        paddingTop={3}
      >
        <Grid item xs={6}>
          <SelectField
            id={`value`}
            name={`attributes.${index}.selectedAttributeValuesVarchars.${valueIndex}`}
            label={companyAttribute?.displayName}
            control={control}
            //readOnly={!hasUpsertPermissions}
            options={options}
            errors={
              errors?.[`selectedAttributeValuesVarchars`]?.[valueIndex]
                ? errors[`selectedAttributeValuesVarchars`][valueIndex]
                : null
            }
            onValueChange={(
              value: MultiSelectOption | (string | number)[] | null
            ) => onValueChange(value, valueIndex)}
          />
        </Grid>
        <Grid item xs={3}>
          {watch(`attributes.${index}.displayName`) === 'Peril' ? (
            <MUIMenu
              attributeValueRemove={attributeValueRemove}
              selectedAttributeVarcharIndex={valueIndex}
            />
          ) : (
            <IconButton onClick={() => deleteAttributeValue(valueIndex)}>
              <Remove />
            </IconButton>
          )}
        </Grid>
      </Grid>
    );
  };

  return <Section>{renderAttributes()}</Section>;
}
